import React, { useEffect, useState, useMemo } from 'react';
import { API_URL } from "../App";

interface Birthday {
    id?: string;
    month: number;
    day: number;
    character: string;
    description: string;
    colorNo: string;
    engVAName: string;
    engVALink: string;
    jpVAName: string;
    jpVALink: string;
    mangaChap: string;
    mangaLink: string;
    animeEp: string;
    animeLink: string;
    movieName: string;
    movieLink: string;
    vigMangaChap: string;
    vigMangaLink: string;
    vigAnimeEp: string;
    vigAnimeLink: string;
    thumbnailLink: string;
    imageLink: string;
    wikiLink: string;
    active: boolean;
}

const emptyBirthday: Birthday = {
    month: 1,
    day: 1,
    character: '',
    description: '',
    colorNo: '#ffffff',
    engVAName: '',
    engVALink: '',
    jpVAName: '',
    jpVALink: '',
    mangaChap: '',
    mangaLink: '',
    animeEp: '',
    animeLink: '',
    movieName: '',
    movieLink: '',
    vigMangaChap: '',
    vigMangaLink: '',
    vigAnimeEp: '',
    vigAnimeLink: '',
    thumbnailLink: '',
    imageLink: '',
    wikiLink: '',
    active: true,
};

function cleanJsonString(input: string): string {
    return input
        .replace(/,\s*([}\]])/g, "$1")
        .replace(/^\s*\/\/.*$/gm, "")
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/[\u0000-\u0019]+/g, "")
        .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
        .trim();
}

function mapJsonToBirthday(data: any): Birthday {
    const month = Number(data.Month);
    const day = Number(data.Day);

    if (!month || !day) {
        throw new Error('JSON must include numeric Month and Day fields.');
    }

    return {
        ...emptyBirthday,
        month,
        day,
        character: data.Character ?? '',
        description: data.Description ?? '',
        colorNo: data.ColorNo ?? emptyBirthday.colorNo,
        engVAName: data.EngVAName ?? '',
        engVALink: data.EngVALink ?? '',
        jpVAName: data.JPVAName ?? '',
        jpVALink: data.JPVALink ?? '',
        mangaChap: data.MangaChap ?? '',
        mangaLink: data.MangaLink ?? '',
        animeEp: data.AnimeEp ?? '',
        animeLink: data.AnimeLink ?? '',
        movieName: data.MovieName ?? '',
        movieLink: data.MovieLink ?? '',
        vigMangaChap: data.VigMangaChap ?? '',
        vigMangaLink: data.VigMangaLink ?? '',
        vigAnimeEp: data.VigAnimeEp ?? '',
        vigAnimeLink: data.VigAnimeLink ?? '',
        thumbnailLink: data.ThumbnailLink ?? '',
        imageLink: data.ImageLink ?? '',
        wikiLink: data.WikiLink ?? '',
        active: true,
    };
}

type BirthdaySortField = 'date' | 'character';

const Birthdays: React.FC = () => {
    const [birthdays, setBirthdays] = useState<Birthday[]>([]);
    const [form, setForm] = useState<Birthday>(emptyBirthday);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [isJsonImportMode, setIsJsonImportMode] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [filterName, setFilterName] = useState('');

    // Tracks which birthday row currently has an action in flight (notify / mark-complete)
    const [busyId, setBusyId] = useState<string | null>(null);

    // NEW: sort state
    const [sortField, setSortField] = useState<BirthdaySortField>('date');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    useEffect(() => {
        void fetchBirthdays();
    }, []);

    async function handleSendNotification(b: Birthday) {
        setError(null);
        setSuccess(null);

        if (!b.id && !b.character) {
            setError('Could not determine which birthday to notify.');
            return;
        }

        const idToUse = b.id;

        try {
            setBusyId(idToUse ?? null);
            const res = await fetch(`${API_URL}/api/birthdays/${idToUse}/notify`, {
                method: 'POST',
                credentials: 'include',
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to send notification');
            }

            setSuccess(`Notification sent for ${b.character}.`);
        } catch (e: any) {
            setError(e.message || 'Failed to send notification');
        } finally {
            setBusyId(null);
        }
    }

    async function handleMarkComplete(b: Birthday) {
        setError(null);
        setSuccess(null);

        if (!b.id) {
            setError('Could not determine which birthday to mark complete.');
            return;
        }

        const confirmed = window.confirm(
            `Mark ${b.character}'s birthday as complete for this year WITHOUT sending the embed?\n\n` +
            `The automatic birthday message will not be sent for ${b.character} this year. ` +
            `Use this when the birthday message failed but you don't want it to fire again.`
        );
        if (!confirmed) return;

        try {
            setBusyId(b.id);
            const res = await fetch(`${API_URL}/api/birthdays/${b.id}/mark-complete`, {
                method: 'POST',
                credentials: 'include',
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to mark birthday complete');
            }

            setSuccess(`${b.character}'s birthday marked complete for this year (no embed sent).`);
        } catch (e: any) {
            setError(e.message || 'Failed to mark birthday complete');
        } finally {
            setBusyId(null);
        }
    }

    async function fetchBirthdays() {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(API_URL + "/api/birthdays", {
                credentials: 'include',
            });
            if (!res.ok) {
                throw new Error(`Failed to load birthdays: ${res.status}`);
            }
            const data = await res.json();
            setBirthdays(Array.isArray(data) ? data : []);
        } catch (e: any) {
            setError(e.message || 'Failed to load birthdays');
        } finally {
            setLoading(false);
        }
    }

    function handleChange<K extends keyof Birthday>(
        field: K,
        value: Birthday[K]
    ) {
        setForm(prev => ({
            ...prev,
            [field]: value,
        }));
        setSuccess(null);
    }

    function handleNumberChange(field: 'month' | 'day', value: string) {
        const num = parseInt(value, 10);
        handleChange(field, isNaN(num) ? ('' as any) : num);
    }

    function startCreateNew() {
        setForm(emptyBirthday);
        setIsEditing(false);
        setError(null);
        setSuccess(null);
    }

    function startEdit(b: Birthday) {
        setForm({
            ...b,
        });
        setIsEditing(true);
        setError(null);
        setSuccess(null);
        setIsJsonImportMode(false);
    }

    function startJsonImport() {
        setIsJsonImportMode(true);
        setJsonText('');
        setError(null);
        setSuccess(null);
    }

    function cancelJsonImport() {
        setIsJsonImportMode(false);
        setJsonText('');
    }

    function handleParseJson() {
        try {
            if (!jsonText.trim()) {
                throw new Error('Please paste some JSON first.');
            }

            const cleaned = cleanJsonString(jsonText);
            const parsed = JSON.parse(cleaned);

            const data = Array.isArray(parsed) ? parsed[0] : parsed;

            const mapped = mapJsonToBirthday(data);

            setForm(mapped);
            setIsEditing(false);
            setIsJsonImportMode(false);
            setError(null);
            setSuccess('JSON imported into form. Review the fields and click Save when ready.');
        } catch (e: any) {
            setError(e.message || 'Failed to parse JSON');
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!form.character || !form.month || !form.day) {
            setError('Character, month, and day are required.');
            return;
        }

        if (form.month < 1 || form.month > 12) {
            setError('Month must be between 1 and 12.');
            return;
        }
        if (form.day < 1 || form.day > 31) {
            setError('Day must be between 1 and 31.');
            return;
        }

        try {
            setSaving(true);

            const payload: Birthday = {
                ...form,
            };

            if (!isEditing) {
                delete (payload as any).id;
            }

            const res = await fetch(
                isEditing && form.id ? `${API_URL}/api/birthdays/${form.id}` : API_URL + '/api/birthdays',
                {
                    method: isEditing ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(
                    text || `Failed to ${isEditing ? 'update' : 'create'} birthday`
                );
            }

            setSuccess(
                isEditing ? 'Birthday updated successfully.' : 'Birthday created successfully.'
            );

            await fetchBirthdays();
            if (!isEditing) {
                setForm(emptyBirthday);
            }
        } catch (e: any) {
            setError(e.message || 'Failed to save birthday');
        } finally {
            setSaving(false);
        }
    }

    const handleSortClick = (field: BirthdaySortField) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortLabel = (field: BirthdaySortField) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const visibleBirthdays = useMemo(() => {
        let list = Array.isArray(birthdays) ? [...birthdays] : [];

        if (filterName.trim()) {
            const term = filterName.toLowerCase();
            list = list.filter((b) =>
                b.character.toLowerCase().includes(term)
            );
        }

        list.sort((a, b) => {
            if (sortField === 'date') {
                const aKey = a.month * 100 + a.day;
                const bKey = b.month * 100 + b.day;
                return sortDirection === 'asc' ? aKey - bKey : bKey - aKey;
            } else {
                const aName = a.character.toLowerCase();
                const bName = b.character.toLowerCase();
                if (aName < bName) return sortDirection === 'asc' ? -1 : 1;
                if (aName > bName) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }
        });

        return list;
    }, [birthdays, filterName, sortField, sortDirection]);

    return (
        <section className="section">
            <h1>Birthdays</h1>

            <p className="mobile-hidden" style={{ opacity: 0.8, marginBottom: '1rem' }}>
                Add or edit character birthdays and their related info here.
            </p>

            <p className="mobile-alert">
                To modify the Birthdays, please access this section on a computer.
            </p>

            {error && (
                <div
                    style={{
                        marginBottom: '1rem',
                        padding: '0.5rem 0.75rem',
                        borderRadius: 4,
                        backgroundColor: 'rgba(255,0,0,0.1)',
                        color: '#b00020',
                    }}
                >
                    {error}
                </div>
            )}

            {success && (
                <div
                    style={{
                        marginBottom: '1rem',
                        padding: '0.5rem 0.75rem',
                        borderRadius: 4,
                        backgroundColor: 'rgba(0,128,0,0.1)',
                        color: '#006400',
                    }}
                >
                    {success}
                </div>
            )}

            {/* Form */}
            <form className="mobile-hidden" onSubmit={handleSubmit} style={{ marginBottom: '2rem' }}>
                {isJsonImportMode ? (
                    <>
                        <h3>Import from JSON</h3>
                        <p style={{ opacity: 0.8, marginBottom: '0.5rem' }}>
                            Paste a JSON object with fields like <code>Month</code>, <code>Day</code>,{' '}
                            <code>Character</code>, etc.
                        </p>
                        <textarea
                            className="textarea"
                            rows={12}
                            value={jsonText}
                            onChange={e => setJsonText(e.target.value)}
                        />
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                            <button
                                type="button"
                                className="button is-primary"
                                onClick={handleParseJson}
                                disabled={saving}
                            >
                                Parse JSON
                            </button>
                            <button
                                type="button"
                                className="button"
                                onClick={cancelJsonImport}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem 1rem',
                                marginBottom: '1rem',
                            }}
                        >
                            {/* Date & basic info */}
                            <div>
                                <label>
                                    Month *
                                    <input
                                        type="number"
                                        min={1}
                                        max={12}
                                        value={form.month || ''}
                                        onChange={e =>
                                            handleNumberChange('month', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Day *
                                    <input
                                        type="number"
                                        min={1}
                                        max={31}
                                        value={form.day || ''}
                                        onChange={e =>
                                            handleNumberChange('day', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Character *
                                    <input
                                        type="text"
                                        value={form.character}
                                        onChange={e =>
                                            handleChange('character', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Color (hex)
                                    <input
                                        type="text"
                                        value={form.colorNo}
                                        onChange={e =>
                                            handleChange('colorNo', e.target.value)
                                        }
                                        className="input"
                                        placeholder="#ffe276"
                                    />
                                </label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input
                                        type="checkbox"
                                        checked={form.active}
                                        onChange={e =>
                                            handleChange('active', e.target.checked)
                                        }
                                    />
                                    Active
                                </label>
                                {form.colorNo && form.colorNo.startsWith('#') && (
                                    <span
                                        style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 4,
                                            border: '1px solid #ccc',
                                            backgroundColor: form.colorNo,
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Description
                                <textarea
                                    value={form.description}
                                    onChange={e =>
                                        handleChange('description', e.target.value)
                                    }
                                    className="textarea"
                                    rows={4}
                                />
                            </label>
                        </div>

                        {/* VA Info */}
                        <h3>Voice Actors</h3>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem 1rem',
                                marginBottom: '1rem',
                            }}
                        >
                            <div>
                                <label>
                                    English VA Name
                                    <input
                                        type="text"
                                        value={form.engVAName}
                                        onChange={e =>
                                            handleChange('engVAName', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    English VA Link
                                    <input
                                        type="url"
                                        value={form.engVALink}
                                        onChange={e =>
                                            handleChange('engVALink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Japanese VA Name
                                    <input
                                        type="text"
                                        value={form.jpVAName}
                                        onChange={e =>
                                            handleChange('jpVAName', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Japanese VA Link
                                    <input
                                        type="url"
                                        value={form.jpVALink}
                                        onChange={e =>
                                            handleChange('jpVALink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Manga / Anime / Movie */}
                        <h3>Canon Appearances</h3>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem 1rem',
                                marginBottom: '1rem',
                            }}
                        >
                            <div>
                                <label>
                                    Manga Chapter
                                    <input
                                        type="text"
                                        value={form.mangaChap}
                                        onChange={e =>
                                            handleChange('mangaChap', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Manga Link
                                    <input
                                        type="url"
                                        value={form.mangaLink}
                                        onChange={e =>
                                            handleChange('mangaLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Anime Episode
                                    <input
                                        type="text"
                                        value={form.animeEp}
                                        onChange={e =>
                                            handleChange('animeEp', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Anime Link
                                    <input
                                        type="url"
                                        value={form.animeLink}
                                        onChange={e =>
                                            handleChange('animeLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Movie Name
                                    <input
                                        type="text"
                                        value={form.movieName}
                                        onChange={e =>
                                            handleChange('movieName', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Movie Link
                                    <input
                                        type="url"
                                        value={form.movieLink}
                                        onChange={e =>
                                            handleChange('movieLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Vigilantes */}
                        <h3>Vigilantes Appearances</h3>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem 1rem',
                                marginBottom: '1rem',
                            }}
                        >
                            <div>
                                <label>
                                    Vigilantes Manga Chapter
                                    <input
                                        type="text"
                                        value={form.vigMangaChap}
                                        onChange={e =>
                                            handleChange('vigMangaChap', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Vigilantes Manga Link
                                    <input
                                        type="url"
                                        value={form.vigMangaLink}
                                        onChange={e =>
                                            handleChange('vigMangaLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Vigilantes Anime Episode
                                    <input
                                        type="text"
                                        value={form.vigAnimeEp}
                                        onChange={e =>
                                            handleChange('vigAnimeEp', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Vigilantes Anime Link
                                    <input
                                        type="url"
                                        value={form.vigAnimeLink}
                                        onChange={e =>
                                            handleChange('vigAnimeLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Media links */}
                        <h3>Media & Wiki</h3>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                gap: '0.75rem 1rem',
                                marginBottom: '1rem',
                            }}
                        >
                            <div>
                                <label>
                                    Thumbnail Link
                                    <input
                                        type="url"
                                        value={form.thumbnailLink}
                                        onChange={e =>
                                            handleChange('thumbnailLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Image Link
                                    <input
                                        type="url"
                                        value={form.imageLink}
                                        onChange={e =>
                                            handleChange('imageLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                            <div>
                                <label>
                                    Wiki Link
                                    <input
                                        type="url"
                                        value={form.wikiLink}
                                        onChange={e =>
                                            handleChange('wikiLink', e.target.value)
                                        }
                                        className="input"
                                    />
                                </label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                type="submit"
                                className="button is-primary"
                                disabled={saving}
                            >
                                {saving
                                    ? isEditing
                                        ? 'Saving...'
                                        : 'Creating...'
                                    : isEditing
                                        ? 'Save Changes'
                                        : 'Create Birthday'}
                            </button>
                            <button
                                type="button"
                                className="button"
                                onClick={startCreateNew}
                                disabled={saving}
                            >
                                New / Clear Form
                            </button>
                            <button
                                type="button"
                                className="button"
                                onClick={startJsonImport}
                                disabled={saving}
                            >
                                Import from JSON
                            </button>
                        </div>
                    </>
                )}
            </form>

            {/* Existing Birthdays */}
            <h2 style={{ marginBottom: '0.5rem' }}>Existing Birthdays</h2>

            <div
                style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, opacity: 0.8 }}>Filter by name:</span>
                    <input
                        type="text"
                        className="input"
                        style={{ maxWidth: 220 }}
                        placeholder="Search character..."
                        value={filterName}
                        onChange={e => setFilterName(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <p>Loading birthdays...</p>
            ) : visibleBirthdays.length === 0 ? (
                <p style={{ opacity: 0.8 }}>No birthdays have been added yet.</p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="table is-fullwidth is-striped birthdays-table">
                        <thead>
                            <tr>
                                <th
                                    className="is-clickable"
                                    onClick={() => handleSortClick('date')}
                                >
                                    Date{sortLabel('date')}
                                </th>
                                <th
                                    className="is-clickable"
                                    onClick={() => handleSortClick('character')}
                                >
                                    Character{sortLabel('character')}
                                </th>
                                <th className='mobile-hidden'>Color</th>
                                <th className='mobile-hidden'>Active</th>
                                <th className='mobile-hidden'>Wiki</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {visibleBirthdays.map(b => (
                                <tr key={b.id || `${b.character}-${b.month}-${b.day}`}>
                                    <td>
                                        {b.day}/{b.month}
                                    </td>
                                    <td>{b.character}</td>
                                    <td className='mobile-hidden'>
                                        {b.colorNo && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: 4,
                                                        border: '1px solid #ccc',
                                                        backgroundColor: b.colorNo,
                                                    }}
                                                />
                                                <span>{b.colorNo}</span>
                                            </span>
                                        )}
                                    </td>
                                    <td className='mobile-hidden'>{b.active ? 'Yes' : 'No'}</td>
                                    <td className='mobile-hidden'>
                                        {b.wikiLink ? (
                                            <a
                                                href={b.wikiLink}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Wiki
                                            </a>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button
                                                type="button"
                                                className="button is-small mobile-hidden"
                                                onClick={() => startEdit(b)}
                                                disabled={busyId === b.id}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="button is-small is-info"
                                                onClick={() => handleSendNotification(b)}
                                                disabled={busyId === b.id}
                                            >
                                                {busyId === b.id ? 'Working...' : 'Send Notification'}
                                            </button>
                                            <button
                                                type="button"
                                                className="button is-small mobile-hidden"
                                                onClick={() => handleMarkComplete(b)}
                                                disabled={busyId === b.id}
                                                title="Mark this birthday as complete for the current year without sending the embed"
                                            >
                                                Mark Complete (No Embed)
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                    </table>
                </div>
            )}
        </section>
    );
};

export default Birthdays;